import React, { useCallback, useRef, useState } from 'react';
import { useEffectOnce } from 'react-use';
import { useDispatch } from 'react-redux';
import useApi from '../_hooks/useApi';
import { Summary } from './Summary/Summary';
import { Insurance } from './Insurance/Insurance';
import { Goals } from './Goals/Goals';
import { HeaderContainer, StyledButton } from './MoneyGuideProStyles';
import { NetWorth } from './NetWorth/NetWorth';
import LoadingMask from '../CommonComponents/LoadingMask';
import { TabProps, Tab, MgpSlug, MgpReportResponse } from './MoneyGuideProTypes';
import { clearCache } from './MoneyGuideProConstants';
import { urlWithParams } from '../CommonUtils/urlWithParams';
import { ErrorState } from '../CommonComponents/ErrorState/ErrorState';
import { useDocumentTitle } from '../_hooks/useDocumentTitle';
import { Tab as OPLTab, TabList, Tabs } from '../CommonComponents/OPL/Tab';
import { useIsEmbeddedMobile } from '../_hooks/useIsEmbeddedMobile';
import { Box } from '../CommonComponents/OPL/Box/Box';
import { useRouteParams } from '../_hooks/useRouteParams';
import { useMgpWarmupPolling } from './hooks/useMgpWarmupPolling';
import { MgpWarmupLoading } from './components/MgpWarmupLoading/MgpWarmupLoading';

const TABS_COMPONENT_MAP = {
  Summary: {
    slug: MgpSlug.Summary,
    component: Summary,
  },
  Insurance: {
    slug: MgpSlug.Insurance,
    component: Insurance,
  },
  Goals: {
    slug: MgpSlug.Goals,
    component: Goals,
  },
  'Net Worth': {
    slug: MgpSlug.Networth,
    component: NetWorth,
  },
};

const getTabContent: (tabId: number, TabsList: Tab[]) => React.FC<TabProps> = (tabId, TabsList) => {
  const selectedTab = TabsList.find(({ id }) => id === tabId);
  return selectedTab?.component || TabsList[0].component;
};

const updateUrlTabSlug = (tab: Tab, clientId: string) => {
  return ADV.util.Router.go2('MoneyGuidePro', {
    clientId,
    tabSlug: tab.slug,
    tabId: tab.id,
  });
};

interface Props {
  clientId: string;
}

export const MoneyGuidePro: React.FC<Props> = props => {
  const { clientId } = props;
  const [tabsList, setTabsList] = useState<Tab[]>();
  const [hasSsoAccess, setHasSsoAccess] = useState(false);
  const [isWarmupInProgress, setIsWarmupInProgress] = useState(false);
  const isMounted = useRef(false);
  const dispatch = useDispatch();
  const isEmbeddedMobile = useIsEmbeddedMobile();

  const { tabSlug, tabId: tabIdString } = useRouteParams<{ tabSlug: string; tabId: string }>();

  const tabId = parseInt(tabIdString, 10);

  const [requestMgpTabs, { loading: mgpTabLoading, errors: mgpTabErrors }] = useApi(
    urlWithParams('/Consumer/MgpReport', { clientId }),
  );

  useEffectOnce(() => {
    isMounted.current = true;

    requestMgpTabs({
      onSuccess: ({
        items,
        hasSsoAccess: responseHasSsoAccess,
        warmupInProgress,
      }: MgpReportResponse) => {
        // The user left while the request was in-flight
        if (!isMounted.current) {
          return;
        }

        setHasSsoAccess(responseHasSsoAccess);
        setIsWarmupInProgress(warmupInProgress);
        const enhancedTabsList = items.map(tab => ({
          ...tab,
          ...TABS_COMPONENT_MAP[tab.name],
        }));

        if (tabSlug) {
          const tab = enhancedTabsList.find(({ slug }) => slug === tabSlug);
          updateUrlTabSlug(tab, clientId);
        } else {
          updateUrlTabSlug(enhancedTabsList[0], clientId);
        }

        setTabsList(enhancedTabsList);
      },
    });

    return () => {
      isMounted.current = false;
      dispatch({ type: clearCache });
    };
  });

  const handleWarmupComplete = useCallback(() => {
    if (isMounted.current) {
      // Tab content is not mounted during warmup; clear any cached slice so MgpReportItem refetches.
      dispatch({ type: clearCache });
      setIsWarmupInProgress(false);
    }
  }, [dispatch]);

  useMgpWarmupPolling(clientId, isWarmupInProgress, handleWarmupComplete);

  useDocumentTitle('Financial Planning');

  if (mgpTabErrors.length > 0) {
    return <ErrorState onRetry={requestMgpTabs} />;
  }

  if (tabSlug === '' || tabsList === undefined || mgpTabLoading) {
    return (
      <>
        <HeaderContainer>
          <LoadingMask width="400px" height="50px" isDark />
          <LoadingMask width="190px" height="50px" isDark />
        </HeaderContainer>
        <LoadingMask width="100%" height="600px" isDark />
      </>
    );
  }

  const TabComponent = getTabContent(tabId, tabsList);
  const selectedIndex = tabsList.findIndex(({ slug }) => slug === tabSlug) || 0;

  return (
    <>
      <Box display={isEmbeddedMobile ? 'block' : 'flex'} justifyContent="space-between" mb="24">
        <Tabs
          selectedIndex={selectedIndex}
          onSelect={index => {
            ADV.util.Router.go2('MoneyGuidePro', {
              clientId,
              tabSlug: tabsList[index].slug,
              tabId: tabsList[index].id,
            });
          }}
          style={{ width: '100%' }}
        >
          <TabList>
            {tabsList.map(({ name }) => (
              <OPLTab isMobile={isEmbeddedMobile} key={name}>
                {name}
              </OPLTab>
            ))}
          </TabList>
        </Tabs>
        {hasSsoAccess && (
          <StyledButton
            isEmbeddedMobile={isEmbeddedMobile}
            variant="primary"
            onClick={() => {
              window.open(`/Consumer/MgpReport/AccessPlanDetails?clientId=${clientId}`, '_blank');
            }}
          >
            Access Plan details
          </StyledButton>
        )}
      </Box>

      {isWarmupInProgress ? (
        <MgpWarmupLoading tabSlug={tabSlug} />
      ) : (
        <TabComponent id={tabId} clientId={clientId} tabList={tabsList} />
      )}
    </>
  );
};
